import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { saveSession } from "../../Core/utils/auth";
import { getCookie, deleteCookieAllDomains } from "../../Core/utils/cookieUtils";
import { decrypt } from "../../Core/utils/others";
import { tokenRole } from "../../Services/tokenManager";

/**
 * Landing route for cross-app SSO handoff (mentor one-click login from
 * Epicenter). The cy-sso session arrives either via the shared
 * `Authorization` + `refresh-token` cookies (prod, `.codeyoung.com`) or, for
 * localhost dev, via an encrypted `_sbx` hash param.
 *
 * Unlike `bootstrapSessionFromCookie` (which bails if a session already
 * exists), this route CLEARS the current session first, so an already
 * logged-in user is switched into the target (mentor) account.
 */
const SESSION_KEYS = ["sandbox_user", "sandbox_user_data", "studentId", "user", "cy-user", "profile_pic"];

const SSOLanding = () => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const redirect = params.get("redirect") || "/home";
    const sbx = params.get("_sbx");

    // 1. Resolve the new tokens — dev hash first, then shared cookies.
    let accessToken;
    let refreshToken;
    if (sbx) {
      try {
        const payload = decrypt(sbx);
        if (payload && payload.ACCESS_TOKEN) {
          accessToken = payload.ACCESS_TOKEN;
          refreshToken = payload.REFRESH_TOKEN;
        }
      } catch {
        /* fall through to cookies */
      }
    }
    if (!accessToken) {
      accessToken = getCookie("Authorization");
      refreshToken = getCookie("refresh-token");
    }

    // 2. Establish the new session and redirect into the app. The old session
    // is cleared ONLY when a new one actually arrived — the handoff cookies are
    // short-lived now, so a stale /sso-login visit (back button / bookmark)
    // finds no tokens and must not wipe a working session.
    if (accessToken && refreshToken) {
      SESSION_KEYS.forEach((k) => {
        try {
          localStorage.removeItem(k);
        } catch {
          /* ignore */
        }
      });
      saveSession({ accessToken, refreshToken });
      // For a NON-STUDENT session the shared cookies are one-shot handoff
      // transport, not an ongoing sync channel (mentor/admin sessions are
      // app-local: their refreshes never re-write the cookies). Delete them
      // once consumed — a lingering copy goes stale after the first token
      // rotation and used to be re-adopted on focus/boot, replacing the live
      // tokens with dead ones and logging the mentor out (~1h after handoff).
      // Student cookies stay: they ARE the cross-app auto-login transport.
      if (tokenRole(accessToken) !== "student") {
        deleteCookieAllDomains("Authorization");
        deleteCookieAllDomains("refresh-token");
      }
      history.replace(redirect);
    } else {
      // Stale visit with no handoff tokens — keep any working session and just
      // move into the app; only a truly signed-out visitor goes to the landing.
      history.replace(localStorage.getItem("sandbox_user") ? redirect : "/");
    }
  }, [history, location.search]);

  return <div style={{ padding: "24px", textAlign: "center" }}>Signing you in…</div>;
};

export default SSOLanding;
